import request from "../secrets-web/src/utils/request";
import global from "../config/global";
import authServices from "./auth";
import user from '../secrets-web/src/services/user';
import coreServices from "../secrets-web/src/services/core";

import { CipherType } from "../secrets-web/src/core-js/src/enums";
import { CipherRequest } from "../secrets-web/src/core-js/src/models/request";
import { CipherView, LoginView, LoginUriView } from "../secrets-web/src/core-js/src/models/view";

async function get_account() {
  return request({
    url: global.endpoint.USERS_ME,
    method: "get",
  });
}

async function update_me(data) {
  return request({
    url: global.endpoint.USERS_ME,
    method: "put",
    data
  });
}

async function user_pm() {
  return await request({
    url: global.endpoint.PM_USERS_ME,
    method: "get",
  });
}

async function update_users_me(data) {
  return request({
    url: global.endpoint.PM_USERS_ME,
    method: 'put',
    data
  })
}

async function delete_me(data) {
  return request({
    url: global.endpoint.PM_USERS_ME_DELETE,
    method: "post",
    data
  });
}

async function new_session(data = {
  email,
  password,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism
}) {
  const deviceId = authServices.device_id() || authServices.random_string();
  authServices.set_device_id(deviceId);
  global.jsCore.cryptoService.clearKeys();
  const key = await coreServices.make_key(
    data.email,
    data.password,
    data.kdf,
    data.kdf_iterations,
    data.kdf_memory,
    data.kdf_parallelism
  )
  const hashedPassword = await global.jsCore.cryptoService.hashPassword(data.password, key)
  return await request({
    url: global.endpoint.USERS_SESSIONS,
    method: "post",
    data: {
      email: data.email,
      client_id: global.constants.CLIENT_ID,
      password: hashedPassword,
      device_name: global.jsCore.platformUtilsService.getDeviceString(),
      device_type: global.jsCore.platformUtilsService.getDevice(),
      device_identifier: deviceId
    }
  });
}

async function createEncryptedMasterPw(masterPw, encKey = null) {
  const cipher = new CipherView()
  cipher.type = CipherType.Login
  const loginData = new LoginView()
  loginData.username = 'locker.io';
  loginData.password = masterPw
  const uriView = new LoginUriView()
  uriView.uri = 'https://.locker.io',
  loginData.uris = [uriView]
  cipher.login = loginData
  cipher.name = 'Locker Master Password'
  const cipherEnc = await global.jsCore.cipherService.encrypt(cipher, encKey)
  const data = new CipherRequest(cipherEnc)
  data.type = CipherType.MasterPassword
  return data
}

function getPasswordStrength(password) {
  return global.jsCore.passwordGenerationService.passwordStrength(password, ['cystack']) || { score: 0 }
}

async function change_master_password(data = {
  username,
  password,
  password_hint,
  login_method,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism
}, newData = {
  password,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism
}) {
  const makeKey = await coreServices.make_key(
    data.username,
    data.password,
    data.kdf,
    data.kdf_iterations,
    data.kdf_memory,
    data.kdf_parallelism
  )
  const passwordHash = await global.jsCore.cryptoService.hashPassword(data.password, makeKey)

  const mewMakeKey = await coreServices.make_key(
    data.username,
    newData.password,
    newData.kdf,
    newData.kdf_iterations,
    newData.kdf_memory,
    newData.kdf_parallelism
  )
  const newPasswordHash = await global.jsCore.cryptoService.hashPassword(newData.password, mewMakeKey)

  let encKey = null
  const existingEncKey = await global.jsCore.cryptoService.getEncKey();
  if (existingEncKey) {
    encKey = await global.jsCore.cryptoService.remakeEncKey(mewMakeKey)
  } else {
    encKey = await global.jsCore.cryptoService.makeEncKey(mewMakeKey)
  }

  const masterPasswordCipher = await createEncryptedMasterPw(newData.password, encKey[0])
  const { score } = getPasswordStrength(newData.password)

  const payload = {
    score: score,
    login_method: data.login_method,
    key: encKey[1].encryptedString,
    master_password_hash: passwordHash,
    new_master_password_hash: newPasswordHash,
    new_master_password_hint: data.password_hint || undefined,
    master_password_cipher: masterPasswordCipher,
    kdf: newData.kdf,
    kdf_iterations: newData.kdf_iterations,
    kdf_memory: newData.kdf_memory,
    kdf_parallelism: newData.kdf_parallelism
  }

  return request({
    url: global.endpoint.PM_USERS_ME_PASSWORD,
    method: 'post',
    data: payload
  })
}

async function register(data = {
  email,
  password,
  password_hint,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism
}) {
  const makeKey = await coreServices.make_key(
    data.email,
    data.password,
    data.kdf,
    data.kdf_iterations,
    data.kdf_memory,
    data.kdf_parallelism
  )
  const encKey = await global.jsCore.cryptoService.makeEncKey(makeKey)
  const keys = await global.jsCore.cryptoService.makeKeyPair(encKey[0])
  const hashedPassword = await global.jsCore.cryptoService.hashPassword(data.password, makeKey)
  const { score } = getPasswordStrength(data.password)

  let payload = {
    score,
    email: data.email,
    password: hashedPassword,
    key: encKey[1].encryptedString,
    public_key: keys[0],
    private_key: keys[1].encryptedString,
    kdf: data.kdf,
    kdf_iterations: data.kdf_iterations,
    kdf_memory: data.kdf_memory,
    kdf_parallelism: data.kdf_parallelism,
    master_password_hint: data.password_hint || undefined,
    timeout: 20160,
  }
  return await request({
    url: global.endpoint.USERS_REGISTER,
    method: "post",
    data: payload
  });
}

export default {
  ...user,
  update_me,
  user_pm,
  update_users_me,
  delete_me,
  get_account,
  new_session,
  change_master_password,
  register
};
